import { Component } from '@theme/component';

/**
 * A custom element that manages scheduled header popups.
 * Handles date-based scheduling, priority selection, and session dismissal.
 *
 * @extends Component
 */
class HeaderPopupsComponent extends Component {
  #dismissedKey = 'header-popups-dismissed';
  #timeoutId = null;
  #testDate = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initPopups();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timeoutId) {
      clearTimeout(this.#timeoutId);
    }
  }

  /**
   * Initializes popups by checking scheduling and showing the highest priority one.
   */
  #initPopups() {
    // Wait a bit for DOM to be fully ready
    requestAnimationFrame(() => {
      const popups = this.querySelectorAll('dialog-component.header-popup');
      if (popups.length === 0) {
        return;
      }

      const activePopups = this.#getActivePopups(popups);
      if (activePopups.length === 0) {
        return;
      }

      const popupToShow = this.#selectHighestPriorityPopup(activePopups);
      if (!popupToShow) {
        return;
      }

      const delay = Number(popupToShow.getAttribute('data-delay')) || 0;
      const delayMs = delay * 1000;

      this.#timeoutId = setTimeout(() => {
        this.#showPopup(popupToShow);
      }, delayMs);
    });
  }

  /**
   * Filters popups to only those that are currently active based on date range.
   *
   * @param {NodeListOf<Element>} popups - All popup elements
   * @returns {Element[]} Active popups
   */
  #getActivePopups(popups) {
    const now = this.#testDate || new Date();
    const dismissed = this.#getDismissedPopups();
    const active = [];

    for (const popup of popups) {
      const popupId = popup.getAttribute('data-popup-id');
      if (dismissed.includes(popupId)) continue;

      const startDate = popup.getAttribute('data-start-date');
      const startTime = popup.getAttribute('data-start-time');
      const endDate = popup.getAttribute('data-end-date');
      const endTime = popup.getAttribute('data-end-time');

      // Skip if no scheduling is set
      if (!startDate && !endDate) continue;

      if (startDate) {
        const start = this.#parseDateTime(startDate, startTime);
        if (start && now < start) continue;
      }

      if (endDate) {
        const end = this.#parseDateTime(endDate, endTime);
        if (end && now > end) continue;
      }

      active.push(popup);
    }

    return active;
  }

  /**
   * Selects the popup with the highest priority from active popups.
   *
   * @param {Element[]} activePopups - Active popup elements
   * @returns {Element | null} Highest priority popup
   */
  #selectHighestPriorityPopup(activePopups) {
    if (activePopups.length === 0) return null;
    if (activePopups.length === 1) return activePopups[0];

    let highestPriority = -1;
    let selectedPopup = null;

    for (const popup of activePopups) {
      const priority = Number(popup.getAttribute('data-priority')) || 1;
      if (priority > highestPriority) {
        highestPriority = priority;
        selectedPopup = popup;
      }
    }

    return selectedPopup;
  }

  /**
   * Shows a popup and sets up dismissal tracking.
   *
   * @param {Element} popupElement - The popup element to show
   */
  #showPopup(popupElement) {
    if (!popupElement || typeof popupElement.showDialog !== 'function') {
      console.warn('Popup element invalid or showDialog not available');
      return;
    }

    const popupId = popupElement.getAttribute('data-popup-id');
    if (!popupId) {
      console.warn('Popup ID not found');
      return;
    }

    // Wait for refs to be initialized - give it a moment
    const dialog = popupElement.refs?.dialog || popupElement.querySelector('dialog');
    if (!dialog) {
      console.warn('Dialog element not found, retrying...');
      setTimeout(() => this.#showPopup(popupElement), 100);
      return;
    }

    // Check if popup has content before showing
    const inner = popupElement.querySelector('.header-popup__inner');
    const hasContent = inner && (
      inner.textContent?.trim() || 
      popupElement.querySelector('.header-popup__image img') ||
      popupElement.querySelector('img')
    );
    
    if (!hasContent) {
      console.warn('Popup has no content, skipping');
      return;
    }

    // Ensure dialog is ready
    if (dialog.open) {
      console.warn('Dialog already open');
      return;
    }

    try {
      // Ensure dialog-component is visible before showing
      popupElement.style.display = 'block';
      popupElement.style.visibility = 'visible';
      
      // Ensure dialog element is ready
      if (dialog) {
        dialog.style.display = '';
        dialog.style.visibility = '';
      }

      popupElement.showDialog();
      console.log('Popup shown successfully');

      // Verify dialog is actually open
      setTimeout(() => {
        if (dialog && !dialog.open) {
          console.warn('Dialog did not open, attempting to restore scroll');
          this.#restoreScroll();
        }
      }, 100);

      const handleClose = () => {
        this.#markAsDismissed(popupId);
        popupElement.removeEventListener('dialog:close', handleClose);
      };

      popupElement.addEventListener('dialog:close', handleClose);
    } catch (error) {
      console.error('Error showing popup:', error);
      // If there's an error, make sure we don't leave scroll locked
      this.#restoreScroll();
    }
  }

  /**
   * Restores page scrolling if it was locked.
   */
  #restoreScroll() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.documentElement.removeAttribute('scroll-lock');
  }

  /**
   * Parses a date and time string, combining them into a Date object.
   *
   * @param {string} dateString - Date string in YYYY-MM-DD format
   * @param {string} timeString - Time string in HH:MM format (optional)
   * @returns {Date | null} Parsed date or null if invalid
   */
  #parseDateTime(dateString, timeString) {
    if (!dateString) return null;

    let dateTimeString = dateString;
    if (timeString) {
      dateTimeString = `${dateString}T${timeString}:00`;
    }

    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return null;

    return date;
  }

  /**
   * Gets the list of dismissed popup IDs from sessionStorage.
   *
   * @returns {string[]} Array of dismissed popup IDs
   */
  #getDismissedPopups() {
    try {
      const dismissed = sessionStorage.getItem(this.#dismissedKey);
      return dismissed ? JSON.parse(dismissed) : [];
    } catch {
      return [];
    }
  }

  /**
   * Marks a popup as dismissed in sessionStorage.
   *
   * @param {string} popupId - The popup ID to mark as dismissed
   */
  #markAsDismissed(popupId) {
    try {
      const dismissed = this.#getDismissedPopups();
      if (!dismissed.includes(popupId)) {
        dismissed.push(popupId);
        sessionStorage.setItem(this.#dismissedKey, JSON.stringify(dismissed));
      }
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Public method to manually trigger a popup by ID (for testing/debugging).
   * Can be called from console: document.querySelector('header-popups-component').showPopupById('block-id')
   *
   * @param {string} popupId - The popup block ID to show
   */
  showPopupById(popupId) {
    const popup = this.querySelector(`dialog-component.header-popup[data-popup-id="${popupId}"]`);
    if (popup) {
      this.#showPopup(popup);
    } else {
      console.warn(`Popup with ID "${popupId}" not found`);
    }
  }

  /**
   * Public method to show the first available popup (for testing/debugging).
   * Can be called from console: document.querySelector('header-popups-component').showFirstPopup()
   */
  showFirstPopup() {
    const popups = this.querySelectorAll('dialog-component.header-popup');
    if (popups.length > 0) {
      this.#showPopup(popups[0]);
    } else {
      console.warn('No popups found');
    }
  }

  /**
   * Public method to set a test date for scheduling (for testing/debugging).
   * Can be called from console: document.querySelector('header-popups-component').setTestDate('2024-12-25T10:00')
   * 
   * @param {string|Date} date - Date string (YYYY-MM-DDTHH:MM) or Date object
   */
  setTestDate(date) {
    this.#testDate = typeof date === 'string' ? new Date(date) : date;
    console.log('Test date set to:', this.#testDate);
    // Re-initialize popups with the new test date
    this.#initPopups();
  }

  /**
   * Public method to clear the test date and use real time.
   * Can be called from console: document.querySelector('header-popups-component').clearTestDate()
   */
  clearTestDate() {
    this.#testDate = null;
    console.log('Test date cleared, using real time');
    // Re-initialize popups with real time
    this.#initPopups();
  }
}

if (!customElements.get('header-popups-component')) {
  customElements.define('header-popups-component', HeaderPopupsComponent);
}

